import { useCallback, useEffect, useRef, useState } from 'react';
import { lessonPlansApi, lessonRunsApi } from '../../services/apiV2';
import { LessonStep } from '../../types/v2';

const HELP_STEP_CONTEXT_REFRESH_MS = 5000;

type HelpStepContext = {
  runId: number | null;
  stepId: number | null;
  step: LessonStep | null;
  runStatus: string;
};

export type HelpStepContextLike = {
  runId?: number | null;
  stepId?: number | null;
  step?: LessonStep | null;
  runStatus?: string | null;
};

const getCurrentStepId = (payload: any): number | null => {
  const rawStepId = payload?.my_current_step_id
    ?? payload?.current_step_id
    ?? payload?.student_state?.current_step_id
    ?? payload?.class_current_step;
  return typeof rawStepId === 'number' ? rawStepId : null;
};

export const hasStepContent = (step: LessonStep | null | undefined): boolean => {
  if (!step || !step.content || typeof step.content !== 'object') return false;
  return Object.keys(step.content).length > 0;
};

export const hasStepContext = (context: HelpStepContextLike | null | undefined): boolean => {
  if (!context?.runId || !context?.stepId || !hasStepContent(context.step)) return false;
  return String(context.runStatus || '').trim().toLowerCase() !== 'completed';
};

export const useHelpStepContext = (runId?: number | null) => {
  const [context, setContext] = useState<HelpStepContext | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const loadContext = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!runId) {
      setContext(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [runResponse, statePayload] = await Promise.all([
        lessonRunsApi.get(runId),
        lessonRunsApi.myState(runId),
      ]);

      if (requestIdRef.current !== requestId) return;

      const runData = runResponse?.data ?? runResponse;
      const currentStepId = getCurrentStepId(statePayload);
      const runStatus = String(statePayload?.status || runData?.status || '').trim().toLowerCase();

      if (!runData?.lesson_plan || !currentStepId || runStatus === 'completed') {
        setContext({
          runId,
          stepId: currentStepId,
          step: null,
          runStatus,
        });
        return;
      }

      const stepsResponse = await lessonPlansApi.steps(runData.lesson_plan);
      if (requestIdRef.current !== requestId) return;

      const steps = Array.isArray(stepsResponse?.data) ? stepsResponse.data as LessonStep[] : [];
      const currentStep = steps.find((step) => step.id === currentStepId) || null;

      setContext({
        runId,
        stepId: currentStepId,
        step: currentStep,
        runStatus,
      });
    } catch {
      if (requestIdRef.current !== requestId) return;
      setContext(null);
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [runId]);

  useEffect(() => {
    void loadContext();
    if (!runId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadContext();
    }, HELP_STEP_CONTEXT_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [loadContext, runId]);

  return {
    context,
    hasStepContext: hasStepContext(context),
    loading,
    reload: loadContext,
  };
};
